import { format } from 'date-fns';

export const ACTIVITY_PAGE_LIMIT = 50;
export const ACTIVITY_BACKGROUND_PAGE_LIMIT = 100;

export type ActivityListEdge = {
  node: {
    createdAt: string;
    kind?: string | null;
    task?: {
      notes?: string | null;
      attachments?: { length: number } | null;
    } | null;
  };
};

/**
 * Clamp the activity `first` query param.
 * Invalid or missing values fall back to the default page size.
 * Values above the backend cap are clamped to 100.
 */
export function parseActivityPageSize(firstParam: string | null): number {
  const parsedFirst = firstParam
    ? parseInt(firstParam, 10)
    : ACTIVITY_PAGE_LIMIT;
  if (Number.isNaN(parsedFirst) || parsedFirst < 1) {
    return ACTIVITY_PAGE_LIMIT;
  }
  return Math.min(parsedFirst, ACTIVITY_BACKGROUND_PAGE_LIMIT);
}

export type ActivityLoaderFlags = {
  showMessages: false | undefined;
  showStatus: false | undefined;
  showTaskNotes: false | undefined;
  excludeAutomation: boolean;
};

/**
 * Activity type toggles default to showing (undefined → backend default true).
 * They are only sent as false when the filter is explicitly `'0'`.
 * Automated activity is hidden unless `filter[showAutomation]=1`.
 */
export function buildActivityLoaderFlags(
  searchParams: URLSearchParams
): ActivityLoaderFlags {
  const showAutomation = searchParams.get('filter[showAutomation]') === '1';
  const showMessagesParam = searchParams.get('filter[showMessages]');
  const showStatusParam = searchParams.get('filter[showStatus]');
  const showTaskNotesParam = searchParams.get('filter[showTaskNotes]');

  return {
    showMessages: showMessagesParam === '0' ? false : undefined,
    showStatus: showStatusParam === '0' ? false : undefined,
    showTaskNotes: showTaskNotesParam === '0' ? false : undefined,
    excludeAutomation: !showAutomation,
  };
}

/**
 * Drop empty status rows that have a task but no notes or attachments.
 * Remaining edges are grouped with `yyyy-MM-dd` date separators.
 */
export function removeUnnecessaryData<T extends ActivityListEdge>(
  activities: T[]
): Array<T | string> {
  const cleanList = activities.filter((activity) => {
    const { kind, task } = activity.node;
    const isEmptyStatus =
      kind === 'status' && task && !(task.notes || task.attachments?.length);
    return !isEmptyStatus;
  });

  return addDateSeparators(cleanList);
}

export function addDateSeparators<T extends { node: { createdAt: string } }>(
  activity: T[]
): Array<T | string> {
  return activity.reduce(
    (prev, curr) => {
      const date = format(new Date(curr.node.createdAt), 'yyyy-MM-dd');

      if (date !== prev.lastDate) {
        prev.lastDate = date;
        prev.all.push(date);
      }
      prev.all.push(curr);
      return prev;
    },
    { lastDate: '', all: [] as Array<T | string> }
  ).all;
}

export function serializeParamsStable(params: URLSearchParams): string {
  const sorted = [...params.entries()].sort((a, b) =>
    a[0] === b[0] ? a[1].localeCompare(b[1]) : a[0].localeCompare(b[0])
  );
  return new URLSearchParams(sorted).toString();
}

export function shouldRevalidateActivity(args: {
  defaultShouldRevalidate: boolean;
  formAction?: string | null;
  currentPathname: string;
  nextPathname: string;
}): boolean {
  if (args.formAction === '/activity/mark-all-as-read') {
    return false;
  }
  if (
    args.currentPathname.includes('/jobs') ||
    args.nextPathname.includes('/jobs')
  ) {
    return false;
  }
  return args.defaultShouldRevalidate;
}
